import React, { KeyboardEvent, memo, useState, useRef } from "react";
import { Input, Button, Form, Icon, Label } from "semantic-ui-react";
import { formatDatetime } from "react-lib/utils/dateUtils";
import { useIntl } from "react-intl";
import ModInfo from "react-lib/appcon/common/ModInfo";

type CardSpecimenCollectorHeaderProps = {
  onEvent?: any;
  isMeBilling?: boolean;
  selectedLabOrderWorking?: any;
  isStatusCancel: boolean;
  loading: boolean;
  labOrder: boolean;
  handleGetLabOrderArrive: () => void;
  // callback
  onClose?: (changed: boolean) => any;
  setBarcodeId?: (id: number | null) => void;
};

const styles = {
  blueLabel: {
    padding: "0.75rem 0.5rem",
    background: "#e6f7fe",
    width: "100%",
    minWidth: "90px",
  },
  fieldLabel: {
    color: "grey",
    minWidth: "max-content",
  },
  fieldLabOrderLabel: {
    fontWeight: "bold",
    minWidth: "max-content",
  },
  fieldLabOrder: {
    padding: "0.5rem 1.5rem",
    background: "#FFE5E5",
    width: "100%",
    minWidth: "max-content",
  },
};

const PaymentStatusLabel = ({ data }: { data?: any }) => {
  if (!data?.payment_status_name) return null;

  const isPaid = data.payment_status_name === "PAID";

  return (
    <Label
      style={{
        padding: "0.75rem 0.5rem",
        background: isPaid ? "green" : "#fffbc5",
        color: isPaid ? "white" : undefined,
        width: "100%",
        minWidth: "90px",
      }}
    >
      {isPaid ? data.payment_status_label : "ยังไม่ชำระเงิน"}
    </Label>
  );
};

const CardSpecimenCollectorHeader = (props: CardSpecimenCollectorHeaderProps) => {
  const intl = useIntl();
  const [searchText, setSearchText] = useState<string>("");
  const [openModInfo, setOpenModInfo] = useState<boolean>(false);

  const disabled = !!(props.isStatusCancel || props.selectedLabOrderWorking?.arrive_time_iso);

  const handleSearchBarcode = (text: string) => {
    props.onEvent({
      message: "ScanBarcodeStickerSpecimen",
      params: {
        action: "centralLabOrderScan",
        searchText: text,
        callback: (status: "SUCCESS" | "ERROR", data: any) => {
          if (status === "SUCCESS") {
            props.setBarcodeId?.(data?.[0]?.pk || null);
            setSearchText("")
          } else {
            setOpenModInfo(true);
          }
        },
      },
    });
  };

  const handleCloseModInfo = () => {
    setOpenModInfo(false);
  };

  const handleKeyPress = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      const value = (e.target as any).value || "";

      handleSearchBarcode(value);
      setSearchText(value);
    }
  };

  if (props.isMeBilling) {
    return (
      <div>
        <ModInfo
          open={openModInfo}
          titleColor={"red"}
          titleName={intl.formatMessage({ id: "ไม่พบรหัสสิ่งส่งตรวจ (Specimen ID)" })}
          onApprove={handleCloseModInfo}
          onClose={handleCloseModInfo}
        ></ModInfo>

        <Form className="lab-order-detail">
          <Form.Group style={{ alignItems: "baseline", margin: 0 }}>
            <Form.Field style={{ minWidth: "max-content" }}>
              <label style={{ fontWeight: "bold", fontSize: "16px" }}>Lab order :</label>
            </Form.Field>

            <Form.Field style={{ minWidth: "max-content" }}>
              <Label style={styles.fieldLabOrder}>
                {props.selectedLabOrderWorking?.full_access_number || "\u00a0"}
              </Label>
            </Form.Field>

            <Form.Field style={{ minWidth: "max-content" }}>
              <Button
                size="mini"
                loading={props.loading}
                disabled={disabled}
                style={{ padding: "10px", background: "#FE9500", color: "white" }}
                onClick={props.handleGetLabOrderArrive}
              >
                {props.labOrder ? (
                  <div style={{ width: "50px" }}>
                    <Icon name="check" />
                  </div>
                ) : (
                  "รับเข้า Lab Order"
                )}
              </Button>
            </Form.Field>
            <Form.Field style={{ flex: 1 }}></Form.Field>
            <Form.Field>
              <Icon className="red" name="close" onClick={props.onClose} link={true}></Icon>
            </Form.Field>
          </Form.Group>

          <Form.Group style={{ margin: "10px 0px" }}>
            <Form.Field style={{ ...styles.fieldLabOrderLabel, marginTop: "25px" }}>
              วันที่
            </Form.Field>
            <Form.Field style={{ marginTop: "18px" }}>
              <Label style={styles.fieldLabOrder}>
                {props.selectedLabOrderWorking?.date || "\u00a0"}
              </Label>
            </Form.Field>

            <Form.Field style={{ ...styles.fieldLabOrderLabel, marginTop: "25px" }}>
              สถานะ
            </Form.Field>
            <Form.Field style={{ marginTop: "18px" }}>
              <Label style={styles.fieldLabOrder}>
                {props.selectedLabOrderWorking?.status_name || "\u00a0"}
              </Label>
            </Form.Field>

            <Form.Field style={{ ...styles.fieldLabOrderLabel, marginTop: "25px" }}>
              Arrive Time
            </Form.Field>
            <Form.Field style={{ marginTop: "18px" }}>
              <Label style={styles.fieldLabOrder}>
                {props.selectedLabOrderWorking?.arrive_time || "\u00a0"}
              </Label>
            </Form.Field>

            <Form.Field style={{ ...styles.fieldLabOrderLabel, marginTop: "25px" }}>
              การชำระเงิน
            </Form.Field>
            <Form.Field style={{ marginTop: "18px" }}>
              <PaymentStatusLabel data={props.selectedLabOrderWorking} />
            </Form.Field>

            <Form.Field style={styles.fieldLabOrderLabel}>
              <label>Scan Specimen (tube) ID code</label>
              <div style={{ display: "flex" }}>
                <Input
                  value={searchText}
                  placeholder="Tube Sticker ID"
                  size="mini"
                  onKeyPress={handleKeyPress}
                  onChange={(e: any, v: any) => {
                    setSearchText(v.value);
                  }}
                ></Input>
                <Button
                  icon="search"
                  onClick={() => handleSearchBarcode(searchText)}
                  style={{ background: "var(--primary-theme-color)", color: "white" }}
                ></Button>
              </div>
            </Form.Field>
          </Form.Group>
        </Form>
      </div>
    );
  }

  // DEFAULT LAYOUT
  return (
    <Form className="lab-order-detail">
      <Form.Group style={{ alignItems: "baseline", margin: 0 }}>
        <Form.Field style={{ minWidth: "max-content" }}>
          <Button
            size="mini"
            color="google plus"
            loading={props.loading}
            disabled={disabled}
            style={{ padding: "10px" }}
            onClick={props.handleGetLabOrderArrive}
          >
            {props.labOrder ? (
              <div style={{ width: "50px" }}>
                <Icon name="check" />
              </div>
            ) : (
              "รับเข้า Lab Order"
            )}
          </Button>
        </Form.Field>

        <Form.Field>
          <Form>
            <Form.Group inline>
              <Form.Field style={styles.fieldLabel}>วันที่</Form.Field>
              <Form.Field>
                <Label style={styles.blueLabel}>
                  {props.selectedLabOrderWorking?.date || "\u00a0"}
                </Label>
              </Form.Field>

              <Form.Field style={styles.fieldLabel}>สถานะ</Form.Field>
              <Form.Field>
                <Label style={styles.blueLabel}>
                  {props.selectedLabOrderWorking?.status_name || "\u00a0"}
                </Label>
              </Form.Field>

              <Form.Field style={styles.fieldLabel}>Arrive Time</Form.Field>
              <Form.Field>
                <Label style={styles.blueLabel}>
                  {formatDatetime(props.selectedLabOrderWorking?.arrive_time_iso, true) || "\u00a0"}
                </Label>
              </Form.Field>

              <Form.Field style={styles.fieldLabel}>Division</Form.Field>
              <Form.Field>
                <Label style={styles.blueLabel}>
                  {props.selectedLabOrderWorking?.order_div_name || "\u00a0"}
                </Label>
              </Form.Field>

              <Form.Field style={styles.fieldLabel}>แพทย์</Form.Field>
              <Form.Field>
                <Label style={styles.blueLabel}>
                  {props.selectedLabOrderWorking?.doctor_name || "\u00a0"}
                </Label>
              </Form.Field>
            </Form.Group>

            <Form.Group inline>
              <Form.Field style={styles.fieldLabel}>การชำระเงิน</Form.Field>

              <Form.Field style={{ marginRight: "1rem" }}>
                <PaymentStatusLabel data={props.selectedLabOrderWorking} />
              </Form.Field>

              <Form.Field style={styles.fieldLabel}>Remark</Form.Field>

              <Form.Field width={11}>{props.selectedLabOrderWorking?.note || ""}</Form.Field>
            </Form.Group>
          </Form>
        </Form.Field>
      </Form.Group>
    </Form>
  );
};

CardSpecimenCollectorHeader.displayName = "CardSpecimenCollectorHeader";

export default memo(CardSpecimenCollectorHeader);
